import { DEFAULT_LOCALE, type SupportedLocale } from "./config";

/** Canonical title values stored in public.titles (FK on users.title). */
export const TITLE_VALUES = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Prof"] as const;
export type TitleValue = (typeof TITLE_VALUES)[number];

const TITLE_LABELS: Record<SupportedLocale, Record<TitleValue, string>> = {
    "en-GB": {
        Mr: "Mr",
        Mrs: "Mrs",
        Ms: "Ms",
        Miss: "Miss",
        Dr: "Dr",
        Prof: "Professor",
    },
    "en-US": {
        Mr: "Mr.",
        Mrs: "Mrs.",
        Ms: "Ms.",
        Miss: "Miss",
        Dr: "Dr.",
        Prof: "Prof.",
    },
    "de-DE": {
        Mr: "Herr",
        Mrs: "Frau",
        Ms: "Frau",
        Miss: "Frau",
        Dr: "Dr.",
        Prof: "Prof.",
    },
    "da-DK": {
        Mr: "Hr.",
        Mrs: "Fru",
        Ms: "Fru",
        Miss: "Frk.",
        Dr: "Dr.",
        Prof: "Prof.",
    },
    "fr-FR": {
        Mr: "M.",
        Mrs: "Mme",
        Ms: "Mme",
        Miss: "Mlle",
        Dr: "Dr",
        Prof: "Pr",
    },
    "it-IT": {
        Mr: "Sig.",
        Mrs: "Sig.ra",
        Ms: "Sig.ra",
        Miss: "Sig.na",
        Dr: "Dott.",
        Prof: "Prof.",
    },
    "es-ES": {
        Mr: "Sr.",
        Mrs: "Sra.",
        Ms: "Sra.",
        Miss: "Srta.",
        Dr: "Dr.",
        Prof: "Prof.",
    },
};

export function isTitleValue(value: string | null | undefined): value is TitleValue {
    return value != null && (TITLE_VALUES as readonly string[]).includes(value);
}

/** Localised label for a stored title enum value. Returns the raw value when unknown. */
export function formatTitle(title: string | null | undefined, locale: SupportedLocale = DEFAULT_LOCALE): string {
    if (!title) return "";
    // Legacy app value before aligning with public.titles
    if (title === "Professor") return TITLE_LABELS[locale].Prof;
    if (isTitleValue(title)) return TITLE_LABELS[locale][title];
    return title;
}

export interface PersonNameInput {
    title?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    formal?: boolean;
    /** Used when name parts are missing, e.g. pre-formatted DB value. */
    fallback?: string | null;
}

/** Build a display name with a localised title prefix. Mirrors public.format_name behaviour. */
export function formatPersonName(input: PersonNameInput, locale: SupportedLocale = DEFAULT_LOCALE): string {
    const { title, firstName, lastName, email, formal = false, fallback } = input;
    const localizedTitle = title ? formatTitle(title, locale) : "";

    if (!title && !firstName && !lastName) {
        if (email?.includes("@")) return email.split("@")[0] ?? "";
        return fallback?.trim() ?? "";
    }

    if (!title) {
        const parts = [firstName, lastName].filter(Boolean);
        if (parts.length > 0) return parts.join(" ");
        return fallback?.trim() ?? "";
    }

    if (formal) {
        const parts = [localizedTitle, lastName].filter(Boolean);
        if (parts.length > 0) return parts.join(" ");
        return fallback?.trim() ?? "";
    }

    const parts = [localizedTitle, firstName, lastName].filter(Boolean);
    if (parts.length > 0) return parts.join(" ");
    return fallback?.trim() ?? "";
}

export interface TitleSelectOption {
    label: string;
    value: TitleValue;
}

/** Select options for title fields. Values stay canonical; labels are localised. */
export function getTitleSelectOptions(locale: SupportedLocale = DEFAULT_LOCALE): TitleSelectOption[] {
    const values = locale === "de-DE" || locale === "fr-FR" ? (["Mr", "Mrs", "Dr", "Prof"] as const) : TITLE_VALUES;
    return values.map((value) => ({
        value,
        label: TITLE_LABELS[locale][value],
    }));
}
